import { Form } from 'antd';
import { ButtonFooter, GroupButtonFooterModalStyled } from 'components/GroupButtonFooterModal';
import Modal from 'components/Modal';
import { CurrencyKeyBoardRef } from 'components/NumberKeyBoard/Currency';
import Text from 'components/Text';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { IApiSplitTicketItem } from 'features/ticketList/services/types/ticket';
import { sumBy } from 'lodash';
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { IStaffItemData } from 'services/shop/types/staff';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { roundToDecimal } from 'utils/roundToDecimal';
import ModalChooseStaffFilter from 'widgets/Staff/DropDown/ModalChooseStaffFilter';
import {
  addLock,
  CategoryType,
  LockedField,
  rebalanceAllCategories,
  removeAllLocksForStaff,
  removeLock,
  updateLockIndicesAfterRemove,
} from './autoBalanceHelpers';
import colorTheme from 'constants/color';
import TableSplitTicket from './TableSplitTicket';

// Constants
const CATEGORY_NAMES = {
  amount: 'Sub-total',
  tip: 'tip',
  discount: 'discount',
  supplyFee: 'supply Fee',
} as const;

// Types
type Ref = {
  open: () => void;
};

type Props = {
  v2?: boolean;
};

type FormValues = { staffs: IApiSplitTicketItem[] };
export const useModalSplitTicketRef = () => useRef<Ref>(null);
export const ModalSplitTicket = forwardRef<Ref, Props>(({ v2 }, ref) => {
  const dispatch = useAppDispatch();
  const currencyRef = useRef<CurrencyKeyBoardRef>(null);
  const details = ticketListSelectors.ticketDetailsData.data();
  const [visible, setVisible] = useState(false);
  const [visibleChooseStaff, setVisibleChooseStaff] = useState(false);
  const [form] = Form.useForm<{ staffs: IApiSplitTicketItem[] }>();
  const staffs = Form.useWatch('staffs', form);
  const setPageLoading = useSetLoadingPage();

  // Auto-balance state management
  const staffsRef = useRef<IApiSplitTicketItem[]>([]);
  const lockedFields = useRef<LockedField[]>([]);
  const draftLockedField = useRef<LockedField | null>(null);
  const isUserEditingRef = useRef(false);

  const filterStaff = useCallback(
    (list: IStaffItemData[]) => {
      return list.filter((o) => !o.anyBody && staffs?.findIndex((s) => o.id === s.staffId) === -1);
    },
    [staffs],
  );

  /**
   * Get category totals for auto-balance calculations
   */
  const getCategoryTotals = useCallback(() => {
    return {
      amount: roundToDecimal(details.subTotal),
      tip: roundToDecimal(details.tip),
      discount: roundToDecimal(details.discountTicket),
      supplyFee: roundToDecimal(details.supplyFee || 0),
    };
  }, [details]);

  /**
   * Handle field value changes and prepare for auto-balance
   */
  const handleFieldChange = useCallback(
    (staffIndex: number, category: CategoryType, value: number) => {
      if (!staffsRef.current) return;

      isUserEditingRef.current = true;

      // Only trigger auto-balance if value actually changed
      const currentValue = staffsRef.current[staffIndex]?.[category] || 0;
      if (roundToDecimal(currentValue) === roundToDecimal(value)) {
        draftLockedField.current = null;
        return;
      }

      // Check if input value exceeds category total
      const totals = getCategoryTotals();
      const totalLockedFields = lockedFields.current
        .filter((field) => field.category === category)
        .reduce((sum, field) => {
          return sum + roundToDecimal(field.value);
        }, 0);

      if (roundToDecimal(totals[category]) - totalLockedFields < value) {
        draftLockedField.current = null;
        form.setFieldValue('staffs', staffsRef.current);
        return;
      }

      draftLockedField.current = { staffIndex, category, value };
    },
    [getCategoryTotals, lockedFields, form],
  );

  /**
   * Handle adding new staff with auto-balance
   */
  const handleAddStaffAutoBalance = useCallback(
    (newStaff: IApiSplitTicketItem) => {
      if (!staffsRef.current || isUserEditingRef.current) return;
      const newStaffs = [...staffs, newStaff];
      const totals = getCategoryTotals();

      // Reset locks for new staff (last index)
      lockedFields.current = removeAllLocksForStaff(lockedFields.current, newStaffs.length - 1);

      // Rebalance all categories
      const balancedStaffs = rebalanceAllCategories(newStaffs, totals, lockedFields.current);
      form.setFieldValue('staffs', balancedStaffs);
      staffsRef.current = balancedStaffs;
    },
    [getCategoryTotals, form, staffs],
  );

  /**
   * Handle removing staff with auto-balance
   */
  const handleRemoveStaffAutoBalance = useCallback(
    (staffIndex: number) => {
      if (!staffsRef.current || isUserEditingRef.current) return;

      const newStaffs = staffsRef.current.filter((_, index) => index !== staffIndex);
      const totals = getCategoryTotals();

      // Update lock indices after removal
      lockedFields.current = updateLockIndicesAfterRemove(lockedFields.current, staffIndex);

      // Rebalance all categories with updated locks
      const balancedStaffs = rebalanceAllCategories(newStaffs, totals, lockedFields.current);

      form.setFieldValue('staffs', balancedStaffs);
      staffsRef.current = balancedStaffs;
    },
    [getCategoryTotals, form],
  );

  /**
   * Create new staff and add with auto-balance
   */
  const handleAddStaff = (o: IStaffItemData) => {
    const newStaff: IApiSplitTicketItem = {
      staffId: o.id,
      amount: 0,
      avatar: o.urlImage,
      staffName: o.nickName ?? o.firstName ?? '',
      discount: 0,
      supplyFee: 0,
      tip: 0,
      service: '',
    };

    handleAddStaffAutoBalance(newStaff);
  };

  /**
   * Handle field focus - remove lock to allow editing
   */
  const handleFocus = useCallback((staffIndex: number, category: CategoryType) => {
    isUserEditingRef.current = true;
    lockedFields.current = removeLock(lockedFields.current, staffIndex, category);
  }, []);

  /**
   * Handle field blur - trigger auto-balance with smart locking logic
   */
  const handleBlur = useCallback(
    (category: CategoryType) => {
      isUserEditingRef.current = false;
      if (!staffsRef.current || !staffs) return;

      const categoryLocks = lockedFields.current.filter((field) => field.category === category);

      // If all fields in category are locked, do nothing
      if (categoryLocks.length === staffs.length) {
        return;
      }

      const totals = getCategoryTotals();

      if (draftLockedField.current) {
        // Check if current field is the last unlocked field
        const unlockedFields = staffs
          .map((_, index) => index)
          .filter((index) => !categoryLocks.some((lock) => lock.staffIndex === index));

        const isLastUnlockedField =
          unlockedFields.length === 1 && unlockedFields[0] === draftLockedField.current.staffIndex;

        if (isLastUnlockedField) {
          // If it's the last unlocked field, only rebalance without adding lock
          const balancedStaffs = rebalanceAllCategories(staffs, totals, lockedFields.current);
          form.setFieldValue('staffs', balancedStaffs);
          staffsRef.current = balancedStaffs;
        } else {
          // If not the last field, add lock and rebalance
          const updatedLocks = addLock(
            lockedFields.current,
            draftLockedField.current.staffIndex,
            draftLockedField.current.category,
            draftLockedField.current.value,
          );
          lockedFields.current = updatedLocks;
          const balancedStaffs = rebalanceAllCategories(staffs, totals, updatedLocks);
          form.setFieldValue('staffs', balancedStaffs);
          staffsRef.current = balancedStaffs;
        }
      } else {
        // No draft lock, just rebalance
        const balancedStaffs = rebalanceAllCategories(staffs, totals, lockedFields.current);
        form.setFieldValue('staffs', balancedStaffs);
        staffsRef.current = balancedStaffs;
      }
      draftLockedField.current = null;
    },
    [staffs, getCategoryTotals, form],
  );

  const handleClose = () => setVisible(false);

  const handleFinish = async (values: FormValues) => {
    handleClose();
    setPageLoading(true);
    const payload: IApiSplitTicketItem[] = values.staffs.map(
      (o) =>
        ({
          amount: o.amount,
          discount: o.discount,
          staffId: o.staffId,
          supplyFee: o.supplyFee,
          tip: o.tip,
          itemName: o.service,
        }) as IApiSplitTicketItem,
    );
    dispatch(
      ticketListActions.splitCloseTicket({
        billId: details.billId,
        data: payload,
      }),
    );
  };

  const checkValid = (staffs: IApiSplitTicketItem[]) => {
    const subTotal = roundToDecimal(sumBy(staffs, (o) => o.amount));
    const tip = roundToDecimal(sumBy(staffs, (o) => o.tip));
    const discount = roundToDecimal(sumBy(staffs, (o) => o.discount));
    const supplyFee = roundToDecimal(sumBy(staffs, (o) => o.supplyFee));

    // Check if all values are zero
    if (!subTotal && !tip && !discount && !supplyFee) {
      return { error: '', valid: false };
    }

    // Validate each category
    const validations = [
      {
        value: subTotal,
        expected: roundToDecimal(details.subTotal),
        name: CATEGORY_NAMES.amount,
      },
      {
        value: tip,
        expected: roundToDecimal(details.tip),
        name: CATEGORY_NAMES.tip,
      },
      {
        value: discount,
        expected: roundToDecimal(details.discountTicket),
        name: CATEGORY_NAMES.discount,
      },
      {
        value: supplyFee,
        expected: roundToDecimal(details.supplyFee || 0),
        name: CATEGORY_NAMES.supplyFee,
      },
    ];

    // Check for values exceeding totals
    for (const validation of validations) {
      if (validation.value > validation.expected) {
        return {
          error: `The ${validation.name} does not match`,
          valid: false,
        };
      }
    }

    // Check for exact matches
    for (const validation of validations) {
      if (validation.value !== validation.expected) {
        return {
          error: `The ${validation.name} does not match`,
          valid: false,
        };
      }
    }

    return { error: '', valid: true };
  };

  useImperativeHandle(ref, () => ({
    open() {
      const dataList = (details?.items || []).map(
        (o) =>
          ({
            staffId: o.staffId,
            amount: 0,
            avatar: o.avatar,
            staffName: o.staffName,
            discount: 0,
            supplyFee: 0,
            tip: 0,
            service: o.services?.[0]?.itemName || '',
          }) as IApiSplitTicketItem,
      );

      // Reset locks và editing flag khi mở modal
      lockedFields.current = [];
      isUserEditingRef.current = false;

      // Rebalance tất cả categories với dữ liệu mới
      const totals = {
        amount: roundToDecimal(details.subTotal),
        tip: roundToDecimal(details.tip),
        discount: roundToDecimal(details.discountTicket),
        supplyFee: roundToDecimal(details.supplyFee || 0),
      };

      const balancedDataList = rebalanceAllCategories(dataList, totals, []);
      form.setFieldValue('staffs', balancedDataList);
      staffsRef.current = balancedDataList;
      setVisible(true);
      setTimeout(() => {
        currencyRef.current?.setValue(formatCurrency(details?.tip || 0));
      }, 100);
    },
  }));

  return (
    <Modal
      modalTitle='Split Master'
      okTitle={'Save'}
      closable={false}
      visible={visible}
      onClose={handleClose}
      width={1680}
      disableOk={false}
      noneBodyStyle
      noneFooterStyle
      className='modal-overflow-unset modal-max-height-unset'
      centered
      footer={null}
      forceRender
      v2={v2}
    >
      <Container className={v2 ? 'v2' : ''}>
        <Form form={form} onFinish={handleFinish}>
          <TableSplitTicket
            details={details}
            handleAddStaffAutoBalance={handleAddStaffAutoBalance}
            handleRemoveStaffAutoBalance={handleRemoveStaffAutoBalance}
            handleFieldChange={handleFieldChange}
            handleFocus={handleFocus}
            handleBlur={handleBlur}
            setVisibleChooseStaff={setVisibleChooseStaff}
            filterStaff={filterStaff}
          />
          <Form.Item shouldUpdate noStyle>
            {({ getFieldsValue }) => {
              const values: FormValues = getFieldsValue();
              const validForm = checkValid(values.staffs);
              const serviceError = values?.staffs?.some((s) => !s.service) ? 'Please select service for all staff' : '';
              return (
                <>
                  {(validForm.error || serviceError) && (
                    <Text
                      style={{
                        opacity: validForm.error || serviceError ? 1 : 0,
                        fontSize: '1.2rem',
                        fontWeight: '500',
                      }}
                      m={1}
                      textAlign='center'
                      variant='CONTENT_1'
                      color='error_6'
                    >
                      {validForm.error || serviceError || 'fail'}
                    </Text>
                  )}
                  <div className='footer-modal'>
                    <GroupButtonFooterModalStyled>
                      <ButtonFooter
                        buttonName='Close'
                        type='DEFAULT'
                        onClick={() => setVisible(false)}
                        cardCashier={v2}
                      />
                      {/* <ButtonFooter buttonName='Reset' type='SECONDARY' onClick={handleReset} cardCashier={v2} /> */}
                      <ButtonFooter
                        buttonName='Ok'
                        type='PRIMARY'
                        disabled={!validForm.valid || isUserEditingRef.current}
                        onClick={() => form.submit()}
                        cardCashier={v2}
                      />
                    </GroupButtonFooterModalStyled>
                  </div>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Container>
      <ModalChooseStaffFilter
        filter={filterStaff}
        visible={visibleChooseStaff}
        onChooseStaff={handleAddStaff}
        onClose={() => setVisibleChooseStaff(false)}
      />
    </Modal>
  );
});
ModalSplitTicket.displayName = 'ModalSplitTicket';
const useModalSplitTicket = (): [React.RefObject<Ref>, JSX.Element] => {
  const ref = useRef<Ref>(null);
  const component = <ModalSplitTicket ref={ref} />;

  return [ref, component];
};
export default useModalSplitTicket;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  .currency-container {
    width: 500px;
  }
  .InputViewStyled {
    justify-content: center;
    input {
      width: 80%;
      flex: unset;
    }
  }
  .footer-modal {
    margin-top: 16px;
    box-shadow: 0px -4px 8px 0px #00000026;
    padding: 16px 16px 24px;
  }

  .table-wrapper {
    max-height: 500px;
    overflow: auto;
    padding: 0 12px;
    .table-thead {
      position: sticky;
      top: 0;
      z-index: 99;
      background: #FFFFFF
    }
      
    tfoot {
      position: sticky;
      bottom: 0px;
      z-index: 99;
      tr th {
        background: ${colorTheme.v2_neutral_2};
        padding: 8px 12px;
        .ant-btn {
          box-shadow: none;
        }
      }
    }
  }

  &.v2 {
    zoom: 1.2;
  }

  .medium-text {
    font-family: Poppins;
    font-weight: 500;
  }
`;
